// ─────────────────────────────────────────────────────────────
// LukaDuka - Location Preference (ver1)
// The phone's copy of "Save location with my entries", per person.
//
// ver1 - 3 November 2026 (Location switch, location-privacy ver5):
//   - The ONLY code that reads or writes the phone's copy of the switch.
//     Every capture path asks getLocationPreference() before it asks the
//     phone for its location. When it says off, nothing asks.
//   - OFF unless the person turned it on. No copy on this phone, no user
//     ID, blocked storage or anything unexpected all mean off.
//   - Answers instantly and never uses the network, so it can never slow
//     down or block a capture, online or offline.
//   - The stored setting lives in profiles.privacy_preferences, key
//     storeGpsCoordinates. Only an exact true means on. The separate
//     locationOnEntries key is NEVER read here - it meant "show a place
//     name field", not "record where I am".
//   - The copy is written only AFTER the database has confirmed:
//       * the Settings switch, once its save succeeds
//       * Router, when the profile loads normally online
//   - Keyed by user ID, so a second person signing in on the same phone
//     never inherits someone else's switch. It holds an on/off flag only -
//     a preference, not personal content.
//
// Known gap: switched off on another device, this phone keeps its old
// copy until it next opens online. Affects offline captures only.
//
// Path: src/shared/services/locationPreference.ts
// ─────────────────────────────────────────────────────────────

const KEY_PREFIX = 'ld-location-pref:'
const ON  = '1'
const OFF = '0'

function keyFor(userId: string): string {
  return `${KEY_PREFIX}${userId}`
}

/**
 * Is "Save location with my entries" on for this person, on this phone?
 * Instant, never touches the network, never throws. Off unless a confirmed
 * "on" has been stored for exactly this user ID.
 */
export function getLocationPreference(userId: string | null | undefined): boolean {
  if (!userId) return false
  try {
    return window.localStorage.getItem(keyFor(userId)) === ON
  } catch {
    // Storage blocked or unavailable - treat as off.
    return false
  }
}

/**
 * Store the phone's copy. Call ONLY after the database has confirmed the
 * value. Never throws - if the phone cannot store it, the switch reads as
 * off here, which is the safe direction.
 */
export function rememberLocationPreference(userId: string, on: boolean): void {
  if (!userId) return
  try {
    window.localStorage.setItem(keyFor(userId), on ? ON : OFF)
  } catch (err) {
    console.warn('[locationPreference] could not store the phone copy:', err)
  }
}

/**
 * Read the switch out of a stored privacy_preferences value (as loaded from
 * the profile). Only an exact true means on - missing, null or anything
 * else is off.
 */
export function locationPreferenceFromStored(privacyPreferences: unknown): boolean {
  if (!privacyPreferences || typeof privacyPreferences !== 'object') return false
  return (privacyPreferences as Record<string, unknown>).storeGpsCoordinates === true
}

/**
 * Refresh the phone's copy from a profile that has just loaded normally.
 * Used by Router after a successful profile load.
 */
export function refreshLocationPreferenceFromProfile(
  userId: string,
  privacyPreferences: unknown
): void {
  rememberLocationPreference(userId, locationPreferenceFromStored(privacyPreferences))
}
